import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  Button,
  Alert,
  Badge,
  ButtonDropdown,
  Spinner
} from '@cloudscape-design/components';
import apiService from '../../services/api';
import AnalystNote from '../../components/reports/AnalystNote';
import ExecBriefingPipeline from '../../components/ExecBriefingPipeline';
import { useFlowStep, useModuleVisit } from '../../hooks/useAnalytics';
import resultsCache from '../../services/resultsCache';

/**
 * ReportPage - Main container for report display and navigation
 * 
 * Implements Task 8.1: ReportPage container component with:
 * - Report generation trigger and progress display
 * - Tabbed navigation (Report, Visualizations, Evidence)
 * - Export dropdown (PDF, Markdown)
 * - Report metadata display (reading time, confidence, documents)
 * 
 * Requirements: 1.5, 5.1
 */
function ReportPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { sessionId } = useParams();
  useFlowStep('demo_completed'); // still fires but deduped by session — belt and braces
  useModuleVisit('exec_briefing');
  
  // State management
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStage, setGenerationStage] = useState('');
  const [exporting, setExporting] = useState(false);

  // Get session ID from URL params or location state
  const currentSessionId = sessionId || location.state?.sessionId;

  useEffect(() => {
    if (currentSessionId) {
      loadExistingReport();
    } else {
      setError('No session ID provided. Please return to research results.');
      setLoading(false);
    }
  }, [currentSessionId]);

  /**
   * Load existing report for the session or show generation option
   */
  const loadExistingReport = async () => {
    try {
      setLoading(true);
      
      // First, try to get existing reports for this session
      const sessionReportsResponse = await apiService.getSessionReports(currentSessionId);
      
      if (sessionReportsResponse.data.reports && sessionReportsResponse.data.reports.length > 0) {
        // Use the most recent report
        const latestReport = sessionReportsResponse.data.reports[0];
        setReportData(latestReport);
        setLoading(false);
      } else {
        // No existing report, show generation option
        setReportData(null);
        setLoading(false);
      }
    } catch (err) {
      console.error('Error loading existing reports:', err);
      // If no reports exist, that's okay - we'll show generation option
      setReportData(null);
      setLoading(false);
    }
  };

  /**
   * Generate new analyst report
   */
  const generateReport = async () => {
    try {
      setGenerating(true);
      setGenerationProgress(0);
      setGenerationStage('Initializing report generation...');
      setError(null);

      // Start report generation
      const generateResponse = await apiService.generateReport({
        session_id: currentSessionId,
        include_visualizations: true
      });

      const reportId = generateResponse.data.report_id;
      setGenerationStage('Analyzing research findings...');
      setGenerationProgress(5);

      // Poll for completion
      let attempts = 0;
      const maxAttempts = 180; // 90 seconds with 0.5s intervals
      
      const pollReport = async () => {
        try {
          const reportResponse = await apiService.getReport(reportId);
          
          // Backend returns 202 while generating (status: "generating" or "initiated")
          // Backend returns 200 with full report when complete
          const data = reportResponse.data;
          
          if (reportResponse.status === 200 && data && data.status === 'completed') {
            // Report is ready — data IS the report (not nested under data.report)
            setReportData(data);
            setGenerationProgress(100);
            setGenerationStage('Executive brief generated!');
            setGenerating(false);
            return;
          }
          
          if (reportResponse.status === 202 || (data && data.status === 'generating')) {
            // Still generating — use real stage from backend
            const stage = data?.stage || 'initializing';
            const stageLabel = data?.stage_label || 'Processing...';
            setGenerationStage(stageLabel);
            
            // Progressive rendering: if backend sent partial sections, render them immediately
            if (data?.partial_sections && Object.keys(data.partial_sections).length > 0) {
              setReportData(prev => ({
                ...(prev || {}),
                report_id: reportId,
                session_id: currentSessionId,
                generated_at: new Date().toISOString(),
                narrative: { sections: data.partial_sections },
                metadata: { confidence_score: 0, word_count: 0, reading_time_minutes: 0, documents_analyzed: [] },
                citations: [],
                status: 'generating'
              }));
            }
            
            // Map stage to progress for pipeline — section callbacks give granular progress
            // 4 sections: exec summary (~25%), SWO (~50%), financials (~75%), risk (~95%)
            const stageProgress = {
              'fetching_data': 5,
              'narrative_generation': 10,
              'Generating executive summary...': 20,
              'Analyzing strengths, weaknesses & opportunities...': 45,
              'Compiling financial analysis...': 65,
              'Assessing risk factors...': 82,
              'visualization_generation': 90,
              'combining_report': 95,
              'initializing': 3,
            };
            const pct = stageProgress[stageLabel] || stageProgress[stage] || Math.min(5 + (attempts * 1.5), 95);
            setGenerationProgress(pct);
            
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(pollReport, 500);
            } else {
              throw new Error('Report generation timeout');
            }
            return;
          }
          
          // Unexpected response — try again
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(pollReport, 500);
          } else {
            throw new Error('Report generation timeout');
          }
        } catch (pollError) {
          // 202 responses may throw in some axios configs — handle gracefully
          if (pollError.response && pollError.response.status === 202) {
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(pollReport, 500);
            }
            return;
          }
          console.error('Error polling report:', pollError);
          setError(`Failed to generate report: ${pollError.message}`);
          setGenerating(false);
        }
      };

      // Start polling after a brief delay
      setTimeout(pollReport, 1000);

    } catch (err) {
      console.error('Error generating report:', err);
      setError(`Failed to generate report: ${err.response?.data?.message || err.message}`);
      setGenerating(false);
    }
  };

  /**
   * Handle export functionality
   */
  const handleExport = async (format) => {
    if (!reportData) return;

    try {
      setExporting(true);
      
      // For now, we'll implement a simple download of the report content
      // In a full implementation, this would call the backend export API
      const exportData = {
        format,
        report_id: reportData.report_id,
        session_id: currentSessionId
      };

      if (format === 'markdown') {
        // Create markdown content
        const markdownContent = createMarkdownExport(reportData);
        downloadFile(markdownContent, `analyst-report-${reportData.report_id}.md`, 'text/markdown');
      } else if (format === 'pdf') {
        // For PDF, we would typically call a backend endpoint
        // For now, show a message that PDF export is being prepared
        alert('PDF export functionality will be implemented in Task 9. For now, please use Markdown export.');
      }

    } catch (err) {
      console.error('Export error:', err);
      setError(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  /**
   * Create markdown export content
   */
  const createMarkdownExport = (report) => {
    const sections = report.narrative.sections;
    let markdown = `# Executive Brief\n\n`;
    markdown += `**Generated:** ${new Date(report.generated_at).toLocaleString()}\n`;
    markdown += `**Session ID:** ${report.session_id}\n`;
    markdown += `**Confidence Score:** ${Math.round(report.metadata.confidence_score * 100)}%\n\n`;

    if (sections.swo_analysis) {
      markdown += `## Strengths, Weaknesses & Opportunities\n\n${sections.swo_analysis}\n\n`;
    }
    if (sections.executive_summary) {
      markdown += `## Executive Summary\n\n${sections.executive_summary}\n\n`;
    }
    if (sections.financial_analysis) {
      markdown += `## Financial Analysis\n\n${sections.financial_analysis}\n\n`;
    }
    if (sections.strategic_analysis) {
      markdown += `## Strategic Analysis\n\n${sections.strategic_analysis}\n\n`;
    }
    if (sections.risk_assessment) {
      markdown += `## Risk Assessment\n\n${sections.risk_assessment}\n\n`;
    }
    if (sections.esg_considerations) {
      markdown += `## ESG Considerations\n\n${sections.esg_considerations}\n\n`;
    }
    if (sections.supporting_evidence) {
      markdown += `## Supporting Evidence\n\n${sections.supporting_evidence}\n\n`;
    }
    if (sections.recommendations) {
      markdown += `## Recommendations\n\n${sections.recommendations}\n\n`;
    }

    return markdown;
  };

  /**
   * Download file helper
   */
  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * Navigate back to review page
   * 
   * Re-hydrate from sessionStorage so ReviewPage can load results even though
   * we don't have the original results object in this component.
   */
  const handleNavigateBack = () => {
    // Try to pass the cached results data so ReviewPage can load immediately
    const data = resultsCache.load();

    navigate('/v2/review', { 
      state: data ? { data } : { sessionId: currentSessionId }
    });
  };

  /**
   * Format reading time
   */
  const formatReadingTime = (minutes) => {
    if (minutes < 1) return '< 1 min read';
    if (minutes === 1) return '1 min read';
    return `${Math.round(minutes)} min read`;
  };

  if (loading) {
    return (
      <Container>
        <SpaceBetween size="l">
          <Header variant="h1">Loading Report</Header>
          <Box textAlign="center">
            <Spinner size="large" />
            <Box marginTop="s">Loading analyst report...</Box>
          </Box>
        </SpaceBetween>
      </Container>
    );
  }

  return (
    <Container>
      <SpaceBetween size="l">
        {/* Header with navigation and actions */}
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="s">
              <Button 
                variant="normal" 
                onClick={handleNavigateBack}
                iconName="arrow-left"
              >
                Back to Results
              </Button>
              {reportData && (
                <>
                  <Button
                    variant="normal"
                    onClick={() => {
                      setReportData(null);
                      setGenerating(false);
                      setGenerationProgress(0);
                      setGenerationStage('');
                      setError(null);
                    }}
                    iconName="refresh"
                  >
                    Regenerate
                  </Button>
                  <ButtonDropdown
                  variant="primary"
                  loading={exporting}
                  items={[
                    {
                      id: 'markdown',
                      text: 'Export as Markdown',
                      description: 'Plain text format for research databases'
                    },
                    {
                      id: 'pdf',
                      text: 'Export as PDF',
                      description: 'Professional format with embedded charts',
                      disabled: true // Will be enabled in Task 9
                    }
                  ]}
                  onItemClick={({ detail }) => handleExport(detail.id)}
                >
                  Export Report
                </ButtonDropdown>
                </>
              )}
              {!reportData && !generating && (
                <Button 
                  variant="primary" 
                  onClick={generateReport}
                  iconName="gen-ai"
                >
                  Generate Executive Brief
                </Button>
              )}
            </SpaceBetween>
          }
        >
          <img 
            src="/images/logo/researchAI_Logo.png" 
            alt="ResearchAI" 
            style={{ height: '40px', width: '40px', marginRight: '16px', verticalAlign: 'middle' }}
          />
          Executive Brief
        </Header>

        {/* Error display */}
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Generation pipeline */}
        {generating && (
          <ExecBriefingPipeline
            generationProgress={generationProgress}
            generationStage={generationStage}
            isGenerating={generating}
            isComplete={false}
            partialSections={reportData?.narrative?.sections || {}}
          />
        )}

        {/* Analyst Note — single-page interleaved layout (replaces tabs) */}
        {(generating || reportData) && (
          <AnalystNote
            reportData={reportData || { narrative: { sections: {} }, metadata: {}, citations: [], visualizations: {} }}
            isGenerating={generating}
            onCitationClick={(citation) => console.log('Citation clicked:', citation)}
          />
        )}

        {/* No report state */}
        {!reportData && !generating && (
          <Alert type="info">
            <SpaceBetween size="m">
              <Box>
                <strong>Ready to Generate Executive Brief</strong>
              </Box>
              <Box>
                Transform your research findings into a comprehensive executive analysis report with:
              </Box>
              <ul style={{ marginLeft: '20px' }}>
                <li>Strengths, Weaknesses & Opportunities (SWO) analysis</li>
                <li>Executive summary and financial analysis</li>
                <li>Strategic insights and risk assessment</li>
                <li>Interactive visualizations and charts</li>
                <li>Properly cited evidence and sources</li>
              </ul>
              <Box>
                <button
                  type="button"
                  onClick={generateReport}
                  style={{
                    background: 'linear-gradient(135deg, #0972d3, #44b9d6)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  ✨ Generate Executive Brief
                </button>
              </Box>
            </SpaceBetween>
          </Alert>
        )}
      </SpaceBetween>
    </Container>
  );
}

export default ReportPage;